import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { registerAdminCommands } from './features/admin/admin-commands';
import { safeReturnPath } from './features/auth/return-url';
import {
  SESSION_ENDED_REASON,
  handleSessionEnded,
  shouldRetry,
} from './features/auth/session-ended';
import { registerNotificationCommands } from './features/notifications/notification-commands';
import { registerOrganisationCommands } from './features/organisation/organisation-commands';
import { registerRequisitionCommands } from './features/requisitions/requisition-commands';
import { registerAssessmentCommands } from './features/assessments/assessment-commands';
import { registerInterviewCommands } from './features/interviews/interview-commands';
import { registerCandidateCommands } from './features/candidates/candidate-commands';
import { registerPoolCommands } from './features/pools/pool-commands';
import { registerReportCommands } from './features/reports/report-commands';
import { registerJobCommands } from './features/jobs/job-commands';
import { registerShellCommands } from './shell/shell-commands';
import { SocketProvider } from './shell/SocketProvider';
import { router } from './router';
import './styles/global.css';

const goToSignIn = async (): Promise<void> => {
  const { pathname, searchStr } = router.state.location;
  await router.navigate({
    to: '/login',
    search: { next: safeReturnPath(`${pathname}${searchStr}`), reason: SESSION_ENDED_REASON },
  });
};

const queryClient: QueryClient = new QueryClient({
  queryCache: new QueryCache({
    onError: (error) => handleSessionEnded(error, queryClient, goToSignIn),
  }),
  mutationCache: new MutationCache({
    onError: (error) => handleSessionEnded(error, queryClient, goToSignIn),
  }),
  defaultOptions: {
    queries: {
      // Never blank data the user can already see: a list keeps showing the
      // previous result while the next one loads, so changing a filter swaps rows
      // in place rather than dropping to a skeleton (CLAUDE.md §5a rule 2).
      placeholderData: (previous: unknown) => previous,
      // Lists are long-lived; the socket invalidates them when they actually
      // change, so polling on focus would be redundant work.
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      retry: shouldRetry,
    },
  },
});

// Feature slices register their own commands at import time; the shell's own go
// in before first paint so the palette is never empty (06-qol §1).
registerShellCommands(router);
// Each feature slice registers its own palette entries (06-qol §1).
registerOrganisationCommands(router);
registerRequisitionCommands(router);
registerJobCommands(router);
registerCandidateCommands(router);
registerPoolCommands(router);
registerReportCommands(router);
registerAssessmentCommands(router);
registerInterviewCommands(router);
registerAdminCommands(router);
registerNotificationCommands(router);

const container = document.getElementById('root');
if (!container) throw new Error('Root element #root is missing from index.html');

createRoot(container).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <SocketProvider>
        {/* The router's `beforeLoad` auth gate reads the session through this
            client, so the gate and every screen share one cache entry. */}
        <RouterProvider router={router} context={{ queryClient }} />
      </SocketProvider>
    </QueryClientProvider>
  </StrictMode>,
);
